import styles from './ReturnsPage.module.css';
import AutoResaleReturnsModalWindow from '@components/modals/AutoResaleReturnsModalWindow';
import AutoResaleOffIcon from '@assets/icons/gear-with-sprocket-grey-21.svg';
import AutoResaleOnIcon from '@assets/icons/gear-with-sprocket-blue-21.svg';
import AwaitingTab from './tabs/AwaitingTab.tsx';
import cn from 'classnames';
import DisposalTab from './tabs/DisposalTab.tsx';
import FilterIcon from '@assets/icons/filter.svg';
import ResaleFilterIcon from '@assets/icons/outline/sparkles.svg?react';
import LastMileStockTab from './tabs/LastMileStockTab.tsx';
import LastMileStoresModalWindow from './modals/LastMileStoresModalWindow';
import Page from '@components/Page';
import ProcessingTab from './tabs/ProcessingTab.tsx';
import ResaleTab from './tabs/ResaleTab';
import ReturnsTab from './tabs/ReturnsTab.tsx';
import useTab from '@hooks/useTab';
import { Button, ButtonSize, ButtonType } from '@components/Button';
import {
  getAutoResaleReturnsData,
  getLastMileWarehouses,
  ONLY_AVAILABLE_FOR_RESALE_FILTER
} from '@api/apiClient/returns';
import { Modal } from '@components/Modal';
import { TabsList, TabsContent } from '@components/Tabs/Tabs';
import {
  useAwaitingCounter,
  useAvailableForResaleCounter,
  useDisposalCounter,
  useLastMileResaleCounter,
  useProcessingCounter,
  useResaleCounter,
  useReturnsCounter
} from '@api/apiClient/returns';
import { useEffect, useState } from 'react';
import { useProfile } from '@contexts/ProfileContext';
import { useQuery } from 'react-query';
import { useSellersCounter } from '@contexts/SellersCounterContext';
import { useTranslation } from 'react-i18next';
import { useFiltersSearchParams } from '@hooks/useFiltersSearchParams';

const ReturnsPage: React.FC = (): React.ReactElement => {
  const [isAutoResaleEnabled, setIsAutoResaleEnabled] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isOpenAutoResaleWindow, setIsOpenAutoResaleWindow] = useState<boolean>(false);
  const [isOpenLastMileStoresWindow, setIsOpenLastMileStoresWindow] = useState<boolean>(false);

  const { account, isFetched: isProfileFetched } = useProfile();
  const { sellersNotFound } = useSellersCounter();
  const { t } = useTranslation();
  const { filters: pageFilters, updateFilter } = useFiltersSearchParams('returns-awaiting');

  const isAvailableForResaleFilterRequested = pageFilters.some((filter) => {
    if (filter.name !== ONLY_AVAILABLE_FOR_RESALE_FILTER) return false;
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    return values.includes('true');
  });

  const {
    data: lastMileWarehousesData,
    isError: isLastMileWarehousesError,
    isFetched: isLastMileWarehousesFetched
  } = useQuery({
    queryKey: ['lastMileWarehouses'],
    queryFn: getLastMileWarehouses,
    retry: 1,
    enabled: isProfileFetched && !sellersNotFound
  });

  const hasLastMileWarehouse = Boolean(lastMileWarehousesData?.warehouses.length);
  const isAvailableForResaleFilterAvailable = isProfileFetched
      && isLastMileWarehousesFetched
      && !isLastMileWarehousesError
      && !hasLastMileWarehouse
      && !sellersNotFound;
  const onlyAvailableForResale = isAvailableForResaleFilterAvailable
      && isAvailableForResaleFilterRequested;

  const { data: awaitingTotal, refetch: awaitingRefetch } = useAwaitingCounter(!sellersNotFound);
  const {
    data: availableForResaleTotal,
    isError: isAvailableForResaleTotalError,
    isFetching: isAvailableForResaleTotalFetching,
    refetch: availableForResaleRefetch
  } = useAvailableForResaleCounter(isAvailableForResaleFilterAvailable);
  const { data: disposalTotal, refetch: disposalRefetch } = useDisposalCounter(!sellersNotFound);
  const { data: processingTotal, refetch: processingRefetch } = useProcessingCounter(!sellersNotFound);
  const { data: resaleTotal, refetch: resaleRefetch } = useResaleCounter(!sellersNotFound);
  const { data: lastMileTotal, refetch: lastMileRefetch } = useLastMileResaleCounter(account?.isTestUser && account?.features.customerReturnsResales && !sellersNotFound);
  const { data: returnsTotal, refetch: returnsRefetch } = useReturnsCounter(!sellersNotFound);

  const {
    data: dataGetAutoResaleReturnsData,
    refetch: refetchGetAutoResaleReturnsData
  } = useQuery({
    queryKey: ['getAutoResaleReturnsData'],
    queryFn: getAutoResaleReturnsData,
    retry: 1,
    enabled: !sellersNotFound
  });

  useEffect(() => {
    dataGetAutoResaleReturnsData && setIsAutoResaleEnabled(dataGetAutoResaleReturnsData.enabled);
  }, [dataGetAutoResaleReturnsData]);

  useEffect(() => {
    const shouldClearFilter = isAvailableForResaleFilterRequested
        && isLastMileWarehousesFetched
        && (isLastMileWarehousesError || hasLastMileWarehouse);

    if (shouldClearFilter) {
      updateFilter({ name: ONLY_AVAILABLE_FOR_RESALE_FILTER, value: [] });
    }
  }, [
    hasLastMileWarehouse,
    isAvailableForResaleFilterRequested,
    isLastMileWarehousesError,
    isLastMileWarehousesFetched,
    updateFilter
  ]);

  const onUpdateAwaitingData = (): void => {
    awaitingRefetch();
    if (isAvailableForResaleFilterAvailable) {
      availableForResaleRefetch();
    }
  };

  const toggleAvailableForResaleFilter = (): void => {
    updateFilter({
      name: ONLY_AVAILABLE_FOR_RESALE_FILTER,
      value: isAvailableForResaleFilterRequested ? [] : ['true']
    });
  };

  const tabs = [
    {
      id: 'AwaitingTab',
      title: t('returnsPage.awaitingTab.title'),
      content: (
          <AwaitingTab
              isFilterOpen={isFilterOpen}
              onlyAvailableForResale={onlyAvailableForResale}
              onUpdateAwaiting={onUpdateAwaitingData}
              onUpdateDisposal={disposalRefetch}
              onUpdateLastMileResale={lastMileRefetch}
              onUpdateResale={resaleRefetch}
              onUpdateReturns={returnsRefetch}
              sellersNotFound={sellersNotFound}
              setIsFilterOpen={setIsFilterOpen}
          />
      ),
      count: awaitingTotal
    },
    {
      id: 'ProcessingTab',
      title: t('returnsPage.processingTab.title'),
      content: (
          <ProcessingTab
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
              onUpdate={processingRefetch}
              sellersNotFound={sellersNotFound}
          />
      ),
      count: processingTotal,
      disable: sellersNotFound
    },
    {
      id: 'ReturnsTab',
      title: t('returnsPage.returnsTab.title'),
      content: (
          <ReturnsTab
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
              onUpdate={returnsRefetch}
              sellersNotFound={sellersNotFound}
          />
      ),
      count: returnsTotal,
      disable: sellersNotFound
    },
    {
      id: 'ResaleTab',
      title: t('returnsPage.resaleTab.title'),
      content: (
          <ResaleTab
              isFilterOpen={isFilterOpen}
              onUpdateAwaiting={onUpdateAwaitingData}
              onUpdateResale={resaleRefetch}
              sellersNotFound={sellersNotFound}
              setIsFilterOpen={setIsFilterOpen}
          />
      ),
      count: resaleTotal,
      disable: sellersNotFound
    },
    ...(account?.isTestUser && account?.features.customerReturnsResales
            ? [{
              id: 'LastMileStockTab',
              title: t('returnsPage.lastMileStockTab.title'),
              content: (
                  <LastMileStockTab
                      isFilterOpen={isFilterOpen}
                      sellersNotFound={sellersNotFound}
                      setIsFilterOpen={setIsFilterOpen}
                  />
              ),
              count: lastMileTotal,
              disable: sellersNotFound
            }]
            : []
    ),
    {
      id: 'DisposalTab',
      title: t('returnsPage.disposalTab.title'),
      content: (
          <DisposalTab
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
              onUpdate={disposalRefetch}
              sellersNotFound={sellersNotFound}
          />
      ),
      count: disposalTotal,
      disable: sellersNotFound
    }
  ];

  const { activeTab, setActiveTab } = useTab(tabs);
  const showActionsBelowTabs = activeTab === 'AwaitingTab' || activeTab === 'ResaleTab';

  return (
      <Page title={t('returnsPage.title')}>
        <div className={cn(styles.tabsRow, showActionsBelowTabs && styles.tabsRowWithActionsBelow)}>
          <TabsList
              onChange={setActiveTab}
              orderArrangement={account?.isTestUser && account?.features.customerReturnsResales ? [1, 0, 4, 3, 2, 5] : [1, 0, 4, 3, 5]}
              tabs={tabs}
              value={activeTab}
          />
          {!sellersNotFound && (
              <div className={styles.filters}>
                {account?.isTestUser && account?.features.customerReturnsResales && (activeTab === 'AwaitingTab' || activeTab === 'ResaleTab') && (
                    <Button
                        onClick={() => setIsOpenLastMileStoresWindow(true)}
                        size={ButtonSize.S}
                        type={ButtonType.ROUNDED}
                        title={t('returnsPage.lastMileResaleBtn')}
                    />
                )}
                {(activeTab === 'AwaitingTab' || activeTab === 'ResaleTab') && (
                    <Button
                        disabled={!dataGetAutoResaleReturnsData}
                        className={cn(styles.autoResaleBtn, isAutoResaleEnabled ? styles.autoResaleBtnOn : styles.autoResaleBtnOff)}
                        icon={isAutoResaleEnabled ? AutoResaleOnIcon : AutoResaleOffIcon}
                        onClick={() => setIsOpenAutoResaleWindow(true)}
                        size={ButtonSize.S}
                        type={ButtonType.ROUNDED}
                        title={t('returnsPage.autoResaleBtn')}
                    />
                )}
                {activeTab === 'AwaitingTab' && isAvailableForResaleFilterAvailable && (
                    <Button
                        disabled={!onlyAvailableForResale && (!availableForResaleTotal || isAvailableForResaleTotalError)}
                        className={cn(
                          styles.resaleFilterBtn,
                          onlyAvailableForResale && styles.resaleFilterBtnActive,
                          !onlyAvailableForResale && (!availableForResaleTotal || isAvailableForResaleTotalError) && styles.resaleFilterBtnDisabled
                        )}
                        icon={ResaleFilterIcon}
                        onClick={toggleAvailableForResaleFilter}
                        pending={isAvailableForResaleTotalFetching}
                        size={ButtonSize.S}
                        type={ButtonType.ROUNDED}
                        title={(
                          <span className={styles.resaleFilterContent}>
                            <span>{t('returnsPage.availableForResaleFilter')}</span>
                            <span className={styles.resaleFilterCount}>{availableForResaleTotal ?? 0}</span>
                          </span>
                        )}
                    />
                )}
                <Button
                    className={cn(isFilterOpen ? styles.filterBtnActive : styles.filterBtnInactive)}
                    icon={FilterIcon}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    size={ButtonSize.S}
                    type={ButtonType.ROUNDED}
                    title={t('filters.filters')}
                />
              </div>
          )}
        </div>
        <TabsContent
            tab={tabs.find((tab) => (tab.id === activeTab))}
        />
        <Modal
            isOpen={isOpenAutoResaleWindow}
            onClickOverlay={() => setIsOpenAutoResaleWindow(false)}
            onClose={() => setIsOpenAutoResaleWindow(false)}
            width={650}
        >
          <AutoResaleReturnsModalWindow
              autoResaleData={dataGetAutoResaleReturnsData!}
              handlerSetIsOpenAutoResaleWindow={setIsOpenAutoResaleWindow}
              handlerRefetchData={refetchGetAutoResaleReturnsData}
          />
        </Modal>
        <Modal
            isOpen={isOpenLastMileStoresWindow}
            onClickOverlay={() => setIsOpenLastMileStoresWindow(false)}
            onClose={() => setIsOpenLastMileStoresWindow(false)}
            width={650}
        >
          <LastMileStoresModalWindow handlerSetIsOpen={setIsOpenLastMileStoresWindow}/>
        </Modal>
      </Page>
  );
};

export default ReturnsPage;
